import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import { Button, Checkbox, Form, Tabs, Divider, Card, Row, Col } from 'antd';
import { MdOutlineKeyboardArrowLeft } from 'react-icons/md';
import { BookOutlined, CalendarOutlined, UsergroupAddOutlined, SettingOutlined, FileTextOutlined, ScheduleOutlined, DollarOutlined, FormOutlined, FolderOpenOutlined, LineChartOutlined, TeamOutlined } from '@ant-design/icons';

import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';

import '../../../css/UserAdmin/Global.css';

// A single, unified source of truth for all staff-related modules
const staffModules = {
    'Manage Applications': [
        'Document Requirements',
        'Exam Schedule Requests',
        'Exam Results',
        'Enrollment Period',
        'Applications',
    ],
    'Manage Accounts': [],
    'Manage Student Records': [],
    'Manage Enrollment': [],
    'Manage Schedule': [],
    'Manage Program': [
        'Strands',
        'Subjects',
        'Sections',
    ],
    'Manage Payments': [
        'Process Payments',
        'Fee Structure',
        'User Ledger',
        'Payment History',
    ],
    'Manage Queue': [],
    'System Logs': [],
    'Create Announcements': [],
};

// Define default modules for all roles, referencing the unified staffModules for staff roles.
const defaultRoleModules = {
    Faculty: {
        'Class Schedule': [],
        'Class Information': [],
        'Handling of Grades': [],
        'Viewing of OTE': [],
    },
    Student: {
        'Student Handbook': [],
        'Certificate of Registration': [],
        'Flowchart': [],
        'Online Class Registration': [],
        'Viewing of Grades': [],
        'Class Schedule': [],
        'Online Teacher\'s Evaluation': [],
        'Student Ledger': [],
        'Enrollment': [],
        'Joining Queue': [],
        'Pay Bills': [],
    },
    'Admissions (Staff)': staffModules,
    'Registrar (Staff)': staffModules,
    'Accounting (Staff)': staffModules,
    'Sub-Admin': staffModules,
    'Super Admin': staffModules,
};

const RoleAccessForm = ({ role }) => {
    const moduleGroups = defaultRoleModules[role] || {};
    const [selectedModules, setSelectedModules] = useState([]);

    const getAllModuleKeys = useCallback(() =>
        Object.entries(moduleGroups).flatMap(([parent, subs]) => [parent, ...subs]),
        [moduleGroups]
    );

    useEffect(() => {
        const fetchRoleData = async () => {
            const defaultModulesForRole = getAllModuleKeys();
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/roles/${role}`);
                if (response.ok) {
                    const result = await response.json();
                    setSelectedModules(result.data.modules || []);
                } else {
                    console.error('Role not found or error fetching, using defaults for this role.');
                    setSelectedModules(defaultModulesForRole);
                }
            } catch (error) {
                console.error('Error fetching role data:', error);
                setSelectedModules(defaultModulesForRole);
            }
        };

        if (role) {
            fetchRoleData();
        }
    }, [role, getAllModuleKeys]);


    const moduleIcons = {
        'Class Schedule': <CalendarOutlined style={{ color: '#C68A00' }} />,
        'Class Information': <BookOutlined style={{ color: '#C68A00' }} />,
        'Handling of Grades': <FormOutlined style={{ color: '#C68A00' }} />,
        'Viewing of OTE': <LineChartOutlined style={{ color: '#C68A00' }} />,
        'Manage Applications': <UsergroupAddOutlined style={{ color: '#C68A00' }} />,
        'Manage Accounts': <TeamOutlined style={{ color: '#C68A00' }} />,
        'Manage Student Records': <FolderOpenOutlined style={{ color: '#C68A00' }} />,
        'Manage Enrollment': <FormOutlined style={{ color: '#C68A00' }} />,
        'Manage Schedule': <ScheduleOutlined style={{ color: '#C68A00' }} />,
        'Manage Program': <SettingOutlined style={{ color: '#C68A00' }} />,
        'Manage Payments': <DollarOutlined style={{ color: '#C68A00' }} />,
        'Manage Queue': <ScheduleOutlined style={{ color: '#C68A00' }} />,
        'System Logs': <FileTextOutlined style={{ color: '#C68A00' }} />,
        'Create Announcements': <FileTextOutlined style={{ color: '#C68A00' }} />,
        'Student Handbook': <BookOutlined style={{ color: '#C68A00' }} />,
        'Certificate of Registration': <FileTextOutlined style={{ color: '#C68A00' }} />,
        'Flowchart': <LineChartOutlined style={{ color: '#C68A00' }} />,
        'Online Class Registration': <FormOutlined style={{ color: '#C68A00' }} />,
        'Viewing of Grades': <FileTextOutlined style={{ color: '#C68A00' }} />,
        'Online Teacher\'s Evaluation': <LineChartOutlined style={{ color: '#C68A00' }} />,
        'Student Ledger': <DollarOutlined style={{ color: '#C68A00' }} />,
        'Enrollment': <UsergroupAddOutlined style={{ color: '#C68A00' }} />,
        'Joining Queue': <TeamOutlined style={{ color: '#C68A00' }} />,
        'Pay Bills': <DollarOutlined style={{ color: '#C68A00' }} />,
    };

    const handleParentToggle = (parent, submodules) => {
        const isChecked = selectedModules.includes(parent);
        const updated = new Set(selectedModules);

        if (isChecked) {
            updated.delete(parent);
            submodules.forEach((sub) => updated.delete(sub));
        } else {
            updated.add(parent);
            submodules.forEach((sub) => updated.add(sub));
        }

        setSelectedModules(Array.from(updated));
    };

    const handleSubToggle = (parent, submodules, changedSub) => {
        const updated = new Set(selectedModules);

        if (updated.has(changedSub)) {
            updated.delete(changedSub);
        } else {
            updated.add(changedSub);
        }

        const hasCheckedSubmodules = submodules.some((sub) => updated.has(sub));

        if (hasCheckedSubmodules) {
            updated.add(parent);
        } else {
            updated.delete(parent);
        }

        setSelectedModules(Array.from(updated));
    };

    const isParentIndeterminate = (parent, submodules) => {
        if (submodules.length === 0) return false;
        const subSelectionCount = submodules.filter((sub) => selectedModules.includes(sub)).length;
        return subSelectionCount > 0 && subSelectionCount < submodules.length;
    };

    const handleSave = async () => {
        let confirmationResult;
        
        const allPossibleModules = getAllModuleKeys();
        const hasFullAccess = selectedModules.length === allPossibleModules.length && allPossibleModules.length > 0;
        const rolesForFullAccessWarning = ['Admissions (Staff)', 'Registrar (Staff)', 'Accounting (Staff)', 'Sub-Admin'];
        
        // Priority 1: Check for granting full access to a non-Super Admin staff role.
        if (rolesForFullAccessWarning.includes(role) && hasFullAccess) {
             confirmationResult = await Swal.fire({
                title: 'High Privilege Warning',
                text: `You are granting all available permissions to the "${role}" role. This level of access is typically reserved for a Super Admin. Are you absolutely sure?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, grant full access!',
                cancelButtonText: 'Cancel'
            });
        }
        // Priority 2: Check if any modules are selected.
        else if (selectedModules.length === 0) {
            confirmationResult = await Swal.fire({
                title: 'Warning: No Modules Selected!',
                text: `You are about to remove ALL access permissions for the "${role}" role. This will prevent them from using any features. Are you sure?`,
                icon: 'error',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, remove all access!',
                cancelButtonText: 'Cancel'
            });
        } else {
            // Standard confirmation for all other cases.
            confirmationResult = await Swal.fire({
                title: 'Confirm Save',
                text: `You are about to update the access permissions for the "${role}" role. Do you want to proceed?`,
                icon: 'info',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, save changes!',
                cancelButtonText: 'Cancel'
            });
        }

        // Only proceed if the user confirmed the appropriate dialog.
        if (confirmationResult && confirmationResult.isConfirmed) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/roles/save-access`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ role, selectedModules }),
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Failed to save access');
                }

                await response.json();
                Swal.fire({
                    icon: 'success',
                    title: 'Saved!',
                    text: `Access permissions for ${role} have been updated successfully.`,
                    timer: 2000,
                    showConfirmButton: false
                });

            } catch (error) {
                console.error('Error saving access:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Save Failed',
                    text: 'An error occurred while saving permissions. Please try again.',
                });
            }
        }
    };

    return (
        <Form layout="vertical">
            <Row gutter={[16, 24]}>
                {Object.entries(moduleGroups).map(([parent, submodules]) => (
                    <Col key={parent} xs={24} sm={12} md={8} lg={6}>
                        <Card
                            title={
                                <span style={{ color: 'white' }}>
                                    {moduleIcons[parent] || <SettingOutlined style={{ color: '#C68A00' }} />} {parent}
                                </span>
                            }
                            headStyle={{ backgroundColor: '#00245A', borderBottom: '1px solid #001d4a' }}
                            bodyStyle={{ paddingTop: 12, minHeight: '150px' }}
                            style={{
                                border: '1px solid #e0e0e0',
                                borderRadius: '12px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                height: '100%',
                            }}
                        >
                            <Checkbox
                                indeterminate={isParentIndeterminate(parent, submodules)}
                                checked={selectedModules.includes(parent)}
                                onChange={() => handleParentToggle(parent, submodules)}
                                style={{ marginBottom: submodules.length > 0 ? 8 : 0, fontWeight: 'bold' }}
                            >
                                {parent}
                            </Checkbox>

                            {submodules.length > 0 && (
                                <div style={{ paddingLeft: 24, marginTop: 8 }}>
                                    {submodules.map((sub) => (
                                        <div key={sub} style={{ marginBottom: 4 }}>
                                            <Checkbox
                                                checked={selectedModules.includes(sub)}
                                                onChange={() => handleSubToggle(parent, submodules, sub)}
                                            >
                                                {sub}
                                            </Checkbox>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </Col>
                ))}
            </Row>

            <Divider />
            <div style={{ textAlign: 'right' }}>
                <Button onClick={() => setSelectedModules(getAllModuleKeys())} style={{ marginRight: 8 }}>
                    Reset to Default
                </Button>
                <Button onClick={() => setSelectedModules([])} style={{ marginRight: 8 }}>
                    Clear All
                </Button>
                <Button type="primary" onClick={handleSave}>
                    Save Changes
                </Button>
            </div>
        </Form>
    );
};

const AccessControl = () => {
    const navigate = useNavigate();
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleBack = () => navigate('/admin/manage-accounts');

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container">
                    <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                </div>
                <main className="admin-main-content-area">
                    <div className="page-title">
                        <div className="arrows" onClick={handleBack}>
                            <MdOutlineKeyboardArrowLeft />
                        </div>
                        <p className="heading">Access Control</p>
                    </div>
                    <Tabs
                        defaultActiveKey="Faculty"
                        type="card"
                        items={Object.keys(defaultRoleModules).map((role) => ({
                            key: role,
                            label: role,
                            children: <RoleAccessForm role={role} />,
                        }))}
                    />
                </main>
            </div>
            <Footer />
        </div>
    );
};

export default AccessControl;